import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MessageGenerateComponent, MessageType } from '@/common/message-generate/message-generate.component';
import { MessageGenerateEditorMode } from '@/common/message-generate/message-generate.model';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { ReservationMailGenerateAdapter } from '@/common/message-generate/adapters/reservation-mail-generate.adapter';
import {
	ReservationMailPreviewAttachment,
	ReservationMailPreviewResponse,
} from '@/new-transport/_models/transport-list.model';
import { MailPreviewDialogComponent } from '@/vindication/invoice/invoice-mail/mail-preview-dialog/mail-preview-dialog.component';
import { BoardingPassMissingReservationIdsPipe } from '@/new-transport/transport-list/mail-generate-dialog/_pipes/boarding-pass-missing-reservation-ids.pipe';

@Component({
	selector: 'app-mail-generate-dialog',
	standalone: true,
	imports: [
		MessageGenerateComponent,
		MatTableModule,
		MatTooltipModule,
		MatProgressBarModule,
		MatButtonModule,
		RouterLink,
		BoardingPassMissingReservationIdsPipe,
	],
	providers: [ReservationMailGenerateAdapter],
	template: `<app-message-generate
			[itemsIds]="reservationIds"
			[type]="MessageType.EMAIL"
			[editorMode]="editorMode"
			[showCcField]="false"
			(closeDialog)="dialogRef.close(true)"
			[previewTemplate]="mailPreviewTpl"
			[adapter]="mailAdapter"
		>
		</app-message-generate>

		<ng-template #mailPreviewTpl let-items let-previewLoader="previewLoader" let-previewErrors="previewErrors">
			@if (items.length) {
				<div class="mail-preview-table-scroll">
					<table class="mail-preview-table" mat-table [dataSource]="items">
						<ng-container matColumnDef="reservationId">
							<th mat-header-cell *matHeaderCellDef>BC</th>
							<td mat-cell *matCellDef="let element">
								<a
									[routerLink]="['/reservation', element.reservationId]"
									target="_blank"
									rel="noopener noreferrer"
								>
									{{ element.reservationId }}
								</a>
							</td>
						</ng-container>

						<ng-container matColumnDef="ownerUser">
							<th mat-header-cell *matHeaderCellDef>Imię i nazwisko</th>
							<td mat-cell *matCellDef="let element">
								{{ element.ownerUser?.firstName }} {{ element.ownerUser?.lastName }}
							</td>
						</ng-container>

						<ng-container matColumnDef="email">
							<th mat-header-cell *matHeaderCellDef>Mail</th>
							<td mat-cell *matCellDef="let element">{{ element.email }}</td>
						</ng-container>

						<ng-container matColumnDef="subject">
							<th mat-header-cell *matHeaderCellDef>Tytuł</th>
							<td
								mat-cell
								*matCellDef="let element"
								matTooltipPosition="right"
								[matTooltip]="element.subject"
							>
								@if (previewLoader) {
									<mat-progress-bar mode="indeterminate"></mat-progress-bar>
								} @else {
									<span class="truncate-message">{{ element.subject }}</span>
								}
							</td>
						</ng-container>

						<ng-container matColumnDef="attachments">
							<th mat-header-cell *matHeaderCellDef>Załączniki</th>
							<td
								mat-cell
								*matCellDef="let element"
								matTooltipPosition="right"
								[matTooltip]="getAttachmentFilenames(element.attachments)"
							>
								<span class="truncate-message">{{ getAttachmentFilenames(element.attachments) }}</span>
							</td>
						</ng-container>

						<ng-container matColumnDef="message">
							<th mat-header-cell *matHeaderCellDef>Wiadomość</th>
							<td mat-cell *matCellDef="let element">
								<button mat-raised-button color="primary" (click)="openPreviewDialog(element)">
									Podgląd
								</button>
							</td>
						</ng-container>

						<tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
						<tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
					</table>
				</div>
			}

			@let boardingPassMissingIds = previewErrors | boardingPassMissingReservationIds;
			@if (boardingPassMissingIds.length) {
				<p class="preview-errors mt-3 mb-0">
					Następujące rezerwacje nie mają wgranej karty pokładowej:
					@for (reservationId of boardingPassMissingIds; track reservationId; let last = $last) {
						<a [routerLink]="['/reservation', reservationId]" target="_blank" rel="noopener noreferrer">
							{{ reservationId }}
						</a>
						@if (!last) {
							,
						}
					}
				</p>
			}
		</ng-template>`,
	styleUrl: './mail-generate-dialog.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MailGenerateDialogComponent {
	readonly reservationIds = inject<number[]>(MAT_DIALOG_DATA);
	readonly dialogRef = inject(MatDialogRef);
	readonly mailAdapter = inject(ReservationMailGenerateAdapter);

	readonly MessageType = MessageType;
	readonly editorMode: MessageGenerateEditorMode = 'ckEditor';

	readonly displayedColumns: string[] = ['reservationId', 'ownerUser', 'email', 'subject', 'attachments', 'message'];

	private readonly dialog = inject(MatDialog);

	openPreviewDialog(element: ReservationMailPreviewResponse): void {
		this.dialog.open(MailPreviewDialogComponent, {
			data: element,
			width: '60%',
		});
	}

	getAttachmentFilenames(attachments: ReservationMailPreviewAttachment[] | undefined): string {
		if (!attachments?.length) {
			return '';
		}
		return attachments.map((attachment) => attachment.filename).join(', ');
	}
}
